import React from 'react';
import {
  FAQ_SECTIONS,
  FAQ_TITLE,
  FAQ_SUBTITLE,
  FAQ_INTRO,
  FAQ_ITEM_COUNT,
} from '../data/faqContent';
import { AnswerStatus, STATUS_META } from '../data/faqTypes';
import { FaqItemCard, StatusBadge } from './FaqItemCard';
import { downloadFaqPDF, formatBrandDate } from './FaqPDF';

// The commercial FAQ, answering the questions that have to be settled before a
// DATA 360 quote can be written. Same content as the PDF export.

const ALL_IDS = FAQ_SECTIONS.flatMap(section => section.items.map(item => item.id));
const STATUSES = Object.keys(STATUS_META) as AnswerStatus[];

const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-9-4l4 4m0 0l4-4m-4 4V4"
    />
  </svg>
);

export const FaqPage: React.FC = () => {
  const [openIds, setOpenIds] = React.useState<Set<string>>(() => new Set(ALL_IDS));
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allOpen = openIds.size === ALL_IDS.length;

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadFaqPDF(formatBrandDate(new Date()));
    } catch (error) {
      // The PDF is generated in the browser, so a failure here is a render or a
      // blob error. Surface it rather than leaving the button spinning.
      const message = error instanceof Error ? error.message : 'Unknown error';
      setDownloadError(`Could not generate the PDF: ${message}`);
    } finally {
      setDownloading(false);
    }
  };

  const counts = STATUSES.map(status => ({
    status,
    count: FAQ_SECTIONS.reduce(
      (total, section) => total + section.items.filter(item => item.status === status).length,
      0
    ),
  }));

  return (
    <div className="px-8 py-6 max-w-[1100px]">
      {/* Title and actions */}
      <div className="flex items-start justify-between gap-6 mb-5">
        <div>
          <h1 className="font-heading text-[22px] font-semibold text-blueprint-blue mb-1">
            {FAQ_TITLE}
          </h1>
          <p className="text-[13px] text-heritage-grey max-w-[640px]">{FAQ_SUBTITLE}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setOpenIds(allOpen ? new Set() : new Set(ALL_IDS))}
            className="bg-white text-text-grey px-4 py-2 rounded-lg text-[13px] font-medium border border-[#E2E5EA] hover:border-heritage-grey hover:bg-page-bg transition-all duration-150"
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-2 bg-hero-red text-white px-4 py-2 rounded-lg text-[13px] font-medium hover:bg-hero-red-60 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DownloadIcon className="h-4 w-4" />
            {downloading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {downloadError && (
        <div className="mb-4 rounded-lg border border-[#E2E5EA] bg-[rgba(196,0,31,0.06)] px-4 py-3 text-[12px] text-readable-red">
          {downloadError}
        </div>
      )}

      {/* Intro and status legend */}
      <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 mb-6">
        <p className="text-[13px] text-text-grey leading-relaxed mb-4">{FAQ_INTRO}</p>
        <div className="text-[10px] font-semibold text-heritage-grey uppercase tracking-[0.06em] mb-3">
          {FAQ_ITEM_COUNT} questions
        </div>
        <div className="flex flex-col gap-2">
          {counts.map(({ status, count }) => (
            <div key={status} className="flex items-center gap-3">
              <StatusBadge status={status} />
              <span className="text-[12px] text-text-grey">
                {STATUS_META[status].meaning}
              </span>
              <span className="text-[12px] text-heritage-grey bg-page-bg px-2 py-0.5 rounded-[10px] ml-auto">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Section jump links */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FAQ_SECTIONS.map(section => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className="px-3 py-1 rounded-full text-[12px] font-medium bg-white border border-[#E2E5EA] text-blueprint-blue hover:border-[#C0C5CE] hover:bg-[#FAFBFC] transition-all"
          >
            {section.title}
          </a>
        ))}
      </div>

      {/* Sections */}
      {FAQ_SECTIONS.map(section => (
        <section key={section.id} id={section.id} className="mb-8 scroll-mt-6">
          <div className="mb-4">
            <h2 className="font-heading text-[18px] font-semibold text-blueprint-blue mb-1">
              {section.title}
            </h2>
            <p className="text-[13px] text-heritage-grey max-w-[720px]">{section.intro}</p>
          </div>
          <div className="flex flex-col gap-3">
            {section.items.map(item => (
              <FaqItemCard
                key={item.id}
                item={item}
                open={openIds.has(item.id)}
                onToggle={() => toggle(item.id)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default FaqPage;
